/**
 * Dot menu next to the audio player — pipeline actions for the audio asset,
 * each visible only while still applicable:
 *
 * - Transcribe: until annotation/* segments exist for this recording
 * - Structure transcript: once segments exist, until structuredTranscript/*
 *   provenance references one of this block's transcription entities
 *   (deleting the structured output tombstones the provenance out of the
 *   current thread, which re-reveals the action — matching structure.ts's
 *   isAlreadyProcessed guard)
 *
 * The menu disappears entirely when nothing is applicable (all done, busy,
 * or read-only view).
 */

import type { EntityID } from '@wovin/core'
import type { Component } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useLiveEntityCollection } from '@wovin/hooks-solid'
import { createMemo, Show } from 'solid-js'
import { setGlobalError } from '../appInit'
import { mistralApiKey } from '../audio/ai-settings'
import { isStructuring, structureAudioBlock } from '../audio/structure'
import { isTranscribing, transcribeAudioBlock } from '../audio/transcribe'
import { useCurrentThread, useReadOnlyState } from '../ui/reactive'
import { notifyToast } from '../ui/utils-ui'
import { Iconify } from './mini-components'
import { useAnnotationSegments } from './TranscriptView'

export const AudioActionsMenu: Component<{ blockID: EntityID }> = (props) => {
	const thread = useCurrentThread()
	const { blockID } = props // ! non-reactive (stable per AudioPlayer instance)
	const readOnly = useReadOnlyState()
	const navigate = useNavigate()

	const segments = useAnnotationSegments(blockID, thread)
	const trxEntities = useLiveEntityCollection({ at: 'transcription/recording', vl: blockID }, [], { thread })
	const provenance = useLiveEntityCollection(
		{ at: 'structuredTranscript/sourceEntity' },
		['structuredTranscript/sourceEntity'] as const,
		{ thread, stripAtPrefix: true },
	)
	const hasStructured = createMemo(() => {
		const trxIds = new Set(trxEntities().keys())
		if (!trxIds.size) return false
		return [...provenance().values()].some(r => trxIds.has(String(r.sourceEntity)))
	})

	const busy = createMemo(() => isTranscribing(blockID) || isStructuring(blockID))
	const hasApiKey = createMemo(() => !!mistralApiKey().trim())
	const showTranscribe = createMemo(() => segments().length === 0)
	const showStructure = createMemo(() => segments().length > 0 && !hasStructured())

	const onSelection = (evt: any) => {
		const action = evt.detail.item.value
		if (!hasApiKey()) {
			notifyToast('No AI provider configured — add a Mistral API key in Settings → AI', 'warning')
			navigate('/settings/ai')
			return
		}
		if (action === 'transcribe') {
			transcribeAudioBlock(blockID, thread)
				.then(trxId => trxId && notifyToast('Transcript ready', 'success'))
				.catch((err) => {
					setGlobalError({
						source: 'Transcribe',
						message: err?.message ?? String(err),
						stack: err?.stack,
						cause: err?.cause?.message,
						timestamp: new Date().toISOString(),
					})
				})
		} else if (action === 'structure') {
			structureAudioBlock(blockID, thread)
				.then(() => notifyToast('Structured notes created', 'success'))
				.catch((err) => {
					setGlobalError({
						source: 'Structure transcript',
						message: err?.message ?? String(err),
						stack: err?.stack,
						cause: err?.cause?.message,
						timestamp: new Date().toISOString(),
					})
				})
		}
	}

	return (
		<Show when={!readOnly() && !busy() && (showTranscribe() || showStructure())}>
			{/* flex on the dropdown host so the trigger icon centers with the player row */}
			<wa-dropdown placement='bottom-end' class='bullet-menu menu-tight flex items-center' onwa-select={onSelection}>
				<Iconify
					slot='trigger'
					name='dots-three-circle'
					size={5}
					class='shrink-0 cursor-pointer opacity-40 transition-opacity hover:opacity-100'
				/>
				<Show when={showTranscribe()}>
					<wa-dropdown-item class='-tight' value='transcribe'>
						<Iconify name='subtitles-bold' slot='icon' />
						Transcribe
					</wa-dropdown-item>
				</Show>
				<Show when={showStructure()}>
					<wa-dropdown-item class='-tight' value='structure'>
						<Iconify name='tree-structure-bold' slot='icon' />
						Structure transcript
					</wa-dropdown-item>
				</Show>
			</wa-dropdown>
		</Show>
	)
}
